import { useLearningDisplayItem } from "../exercises/LearningDisplayBoundary";
import { useRecommendationRunStarter } from "../exercises/recommendation-context";
import { useEffect, useRef, useState } from "react";
import type { QuizExerciseConfig } from "@calega/content";
import { quizAnswerIsCorrect, quizExercise } from "@calega/exercise-engine";
import type { Account } from "../lib/types";
import { useRealtime } from "../realtime/RealtimeContext";
import { ExerciseWaitingCard } from "./ExerciseWaitingCard";
import { submitExerciseAnswers } from "../exercises/learning-api";
import { CompletedRunActions } from "./CompletedRunActions";
import { CompletedRunHistory } from "./CompletedRunHistory";
import { RunCompletion, useRunCompletion } from "./RunCompletion";

export function Quiz({ account, config, pageId }: { account: Account; config: QuizExerciseConfig; pageId: string }) {
  const startExerciseRun = useRecommendationRunStarter();
  const completion = useRunCompletion();
  const realtime = useRealtime();
  const facilitator = account.roles.some((role) => role === "ADMIN" || role === "ORGANIZATION" || role === "THERAPIST");
  const follower = Boolean(realtime.session && !facilitator);
  const canControl = !realtime.session || facilitator;
  const [state, setState] = useState(() => quizExercise.create(config));
  const [runId, setRunId] = useState("");
  useLearningDisplayItem(runId, "0", !state.submitted && Boolean(runId));
  const [trackingMessage, setTrackingMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const savedAnswers = useRef(new Set<string>());

  async function startRun() {
    const result = await startExerciseRun<{ runId: string }>({ contentPageId: pageId });
    const next = quizExercise.create(config);
    setState(next);
    setRunId(result.runId);
    savedAnswers.current.clear();
    setTrackingMessage("");
    realtime.publishExercise({ exerciseType: "quiz", pageId, runId: result.runId, state: next });
  }

  useEffect(() => {
    const synced = realtime.state.exercise as { exerciseType?: string; pageId?: string; runId?: string; state?: typeof state } | null;
    if (synced?.exerciseType === "quiz" && synced.pageId === pageId) {
      if (synced.state) setState(synced.state);
      setRunId(synced.runId ?? "");
    }
  }, [realtime.state.exercise, pageId]);

  function answer(questionId: string, optionId: string, selected: boolean) {
    if (submitting) return;
    const next = quizExercise.reduce(config, state, { type: "answer", questionId, optionId, selected });
    setState(next);
    realtime.publishExercise({ exerciseType: "quiz", pageId, runId, state: next });
  }

  async function submit() {
    if (!runId || submitting) return;
    setSubmitting(true);
    const next = quizExercise.reduce(config, state, { type: "submit" });
    try {
      const inputs = Object.entries(next.answers).filter(([questionId, optionIds]) => !savedAnswers.current.has(JSON.stringify([runId, questionId, optionIds])))
        .map(([questionId, optionIds]) => ({ type: "quiz" as const, questionId, optionIds }));
      await submitExerciseAnswers(runId, inputs);
      for (const input of inputs) savedAnswers.current.add(JSON.stringify([runId, input.questionId, input.optionIds]));
      await completion.complete(runId, () => {
        setState(next);
        realtime.publishExercise({ exerciseType: "quiz", pageId, runId, state: next });
        setTrackingMessage("");
      });
    } catch (error) {
      setTrackingMessage(error instanceof Error ? error.message : "Der Übungslauf konnte nicht gespeichert werden.");
    } finally { setSubmitting(false); }
  }

  function returnToStart() {
    const next = quizExercise.create(config);
    setState(next);
    setTrackingMessage("");
    setRunId("");
    realtime.publishExercise({ exerciseType: "quiz", pageId, runId: "", state: null });
  }

  async function closeCompletedRun(repeat: boolean) {
    if (!runId || !state.submitted) return;
    if (repeat) await startRun();
    else returnToStart();
  }

  if (completion.status.phase !== "idle") return <RunCompletion completion={completion} />;

  if (follower && !runId) return <ExerciseWaitingCard className="quiz" />;

  if (!runId) return <section className="quiz">
    <div className="quiz-instructions learning-text">{config.instructions}</div>
    <button className="primary" disabled={!canControl} onClick={() => startRun().catch((error) => setTrackingMessage(error.message))}>Quiz starten</button>
    {trackingMessage && <div className="alert error">{trackingMessage}</div>}
  </section>;

  return (
    <section className="quiz">
      <div className="quiz-instructions learning-text">{config.instructions}</div>
      {config.questions.map((question, index) => {
        const selected = state.answers[question.id] ?? [];
        const isCorrect = quizAnswerIsCorrect(question, selected);
        const multiple = question.correctOptionIds.length > 1;
        return (
          <fieldset className="quiz-question" key={question.id}>
            <legend className="learning-text"><span>{index + 1}</span>{question.prompt}</legend>
            <div className="quiz-options">
              {question.options.map((option) => {
                const resultClass = state.submitted
                  ? question.correctOptionIds.includes(option.id) ? "correct" : selected.includes(option.id) ? "wrong" : ""
                  : "";
                return (
                  <label className={`quiz-option learning-text ${resultClass}`} key={option.id}>
                    <input
                      type={multiple ? "checkbox" : "radio"}
                      name={question.id}
                      value={option.id}
                      checked={selected.includes(option.id)}
                      disabled={state.submitted || submitting}
                      onChange={(event) => answer(question.id, option.id, event.target.checked)}
                    />
                    {option.label}
                  </label>
                );
              })}
            </div>
            {multiple && !state.submitted && <small className="field-help">Mehrere Antworten können richtig sein.</small>}
            {state.submitted && <div className={`answer-feedback learning-text ${isCorrect ? "correct" : "wrong"}`}>{isCorrect ? "Richtig." : "Noch nicht richtig."} {question.explanation}</div>}
          </fieldset>
        );
      })}
      {trackingMessage && <div className="alert info">{trackingMessage}</div>}
      {state.submitted ? (
        <><div className="quiz-result"><strong>{state.correct} von {state.total} richtig</strong><p>Der Durchlauf ist abgeschlossen.</p></div>{canControl && <CompletedRunActions onFinish={() => closeCompletedRun(false)} onRepeat={() => closeCompletedRun(true)} onError={setTrackingMessage} />}{state.submitted && <CompletedRunHistory key={runId} runId={runId} />}</>
      ) : (
        <button className="primary" disabled={!runId || submitting} onClick={() => submit()}>{submitting ? "Antworten werden gespeichert …" : runId ? "Antworten auswerten" : "Übung wird vorbereitet …"}</button>
      )}
    </section>
  );
}
